import React from "react";
import kiraSuperior from "../../../assets/kira-superior.png";
import type { AppMode, RecordingStatus } from "../hooks/useTranslator";

interface AppHeaderProps {
  appMode: AppMode;
  recording: boolean;
  voiceEnabled: boolean;
  pipActive: boolean;
  status: RecordingStatus;
  statusText: string;
  srcLang: string;
  tgtLang: string;
  theme: "light" | "dark";
  meetingTranscriptLength: number;
  getStatusColorClass: () => string;
  setAppMode: (mode: AppMode) => void;
  setVoiceEnabled: (v: boolean) => void;
  setSrcLang: (lang: string) => void;
  setTgtLang: (lang: string) => void;
  onToggleRecording: () => void;
  onTogglePip: () => void;
  onToggleTheme: () => void;
  onOpenDocs: () => void;
  onOpenSummary: () => void;
  onLogout: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  appMode,
  recording,
  voiceEnabled,
  pipActive,
  status,
  statusText,
  srcLang,
  tgtLang,
  theme,
  meetingTranscriptLength,
  getStatusColorClass,
  setAppMode,
  setVoiceEnabled,
  setSrcLang,
  setTgtLang,
  onToggleRecording,
  onTogglePip,
  onToggleTheme,
  onOpenDocs,
  onOpenSummary,
  onLogout,
}) => {
  const handleModeChange = (mode: AppMode) => {
    if (recording) {
      alert("Por favor, detén la sesión antes de cambiar de modo.");
      return;
    }
    setAppMode(mode);
  };

  return (
    <header className="flex items-center justify-between h-[56px] px-5 border-b border-[var(--border)] bg-[var(--sidebar-bg)] backdrop-blur-md z-10 flex-shrink-0 gap-[16px] transition-colors duration-300">
      {/* Left: Brand logo + status */}
      <div className="brand flex items-center gap-3">
        {/* macOS window controls */}
        <div className="traffic-lights flex gap-2 items-center mr-2 flex-shrink-0">
          <span className="w-3 h-3 rounded-full bg-[var(--mac-close)]" title="Cerrar" />
          <span className="w-3 h-3 rounded-full bg-[var(--mac-minimize)]" title="Minimizar" />
          <span className="w-3 h-3 rounded-full bg-[var(--mac-maximize)]" title="Maximizar" />
        </div>
        <img src={kiraSuperior} className="h-[38px] w-auto object-contain bg-transparent flex-shrink-0" alt="Kira" />
        <div className={`status-pill inline-flex items-center gap-[6px] font-mono text-[9px] font-medium tracking-[0.02em] uppercase rounded-[100px] py-[3px] px-[9px] ml-3 transition-all duration-300 border before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full ${status === "connecting"
            ? "border-yellow-500/30 text-yellow-550 bg-yellow-500/5 before:bg-yellow-500"
            : status === "connected"
              ? "border-emerald-500/30 text-[var(--success)] bg-[rgba(48,209,88,0.05)] before:bg-[var(--success)] before:shadow-[0_0_6px_var(--success)] before:animate-pulse"
              : status === "error"
                ? "border-rose-500/30 text-[var(--danger)] bg-rose-500/5 before:bg-[var(--danger)]"
                : "border-[var(--border)] text-[var(--text-muted)] bg-white/[0.04] before:bg-[var(--text-dim)]"
          }`}>
          {statusText}
        </div>
      </div>

      {/* Right: Controls */}
      <div className="controls flex items-center gap-2">
        {/* Mode toggle */}
        <div className="mac-segmented-control flex bg-[var(--border)] border border-[var(--border)] p-[2px] rounded-[6px] mr-2">
          <button
            onClick={() => handleModeChange("traductor")}
            className={`text-[11px] font-medium py-[4px] px-[12px] rounded-[4px] cursor-pointer transition-all duration-200 outline-none ${appMode === "traductor"
                ? "bg-[var(--surface)] text-[var(--text)] shadow-[0_1px_2px_rgba(0,0,0,0.1)]"
                : "text-[var(--text-muted)] hover:text-[var(--text)]"
              }`}
          >
            Traductor + Minuta
          </button>
          <button
            onClick={() => handleModeChange("minuta")}
            className={`text-[11px] font-medium py-[4px] px-[12px] rounded-[4px] cursor-pointer transition-all duration-200 outline-none ${appMode === "minuta"
                ? "bg-[var(--surface)] text-[var(--text)] shadow-[0_1px_2px_rgba(0,0,0,0.1)]"
                : "text-[var(--text-muted)] hover:text-[var(--text)]"
              }`}
          >
            Solo Minuta
          </button>
        </div>

        {/* Language selectors */}
        <div className="select-wrap flex items-center gap-[6px] bg-[var(--surface)] border border-[var(--border)] rounded-[6px] py-[4px] px-[10px] transition-all duration-200 hover:bg-[var(--surface-hover)]">
          <span className="select-label text-[10px] font-mono font-medium text-[var(--text-dim)] uppercase select-none">De</span>
          <select
            value={srcLang}
            onChange={(e) => setSrcLang(e.target.value)}
            disabled={recording}
            className="bg-transparent border-none text-[var(--text)] text-[13px] font-medium outline-none cursor-pointer pr-[4px]"
          >
            <option value="auto" className="bg-[var(--window-bg)] text-[var(--text)]">✨ Auto-detectar (Multilingüe)</option>
            <option value="en" className="bg-[var(--window-bg)] text-[var(--text)]">English</option>
            <option value="es" className="bg-[var(--window-bg)] text-[var(--text)]">Español</option>
          </select>
        </div>

        <span className="arrow text-[var(--text-dim)] text-[12px] px-[2px] select-none">→</span>

        <div className="select-wrap flex items-center gap-[6px] bg-[var(--surface)] border border-[var(--border)] rounded-[6px] py-[4px] px-[10px] transition-all duration-200 hover:bg-[var(--surface-hover)]">
          <span className="select-label text-[10px] font-mono font-medium text-[var(--text-dim)] uppercase select-none">
            {appMode === "minuta" ? "Minuta en" : "A"}
          </span>
          <select
            value={tgtLang}
            onChange={(e) => setTgtLang(e.target.value)}
            disabled={recording}
            className="bg-transparent border-none text-[var(--text)] text-[13px] font-medium outline-none cursor-pointer pr-[4px]"
          >
            <option value="es" className="bg-[var(--window-bg)] text-[var(--text)]">Español</option>
            <option value="en" className="bg-[var(--window-bg)] text-[var(--text)]">English</option>
          </select>
        </div>

        {/* Start / Stop */}
        <button
          onClick={onToggleRecording}
          className={`inline-flex items-center justify-center gap-[6px] py-[6px] px-[14px] rounded-[6px] border-none text-[13px] font-medium cursor-pointer transition-all duration-150 active:scale-95 text-white shadow-[0_1px_3px_rgba(0,0,0,0.15)] hover:shadow-[0_2px_5px_rgba(0,0,0,0.25)] ${recording
              ? "bg-[var(--danger)] hover:bg-[#e03b30]"
              : "bg-[var(--accent)] hover:bg-[#006ee6]"
            }`}
        >
          {recording ? "■ Stop" : "▶ Start"}
        </button>

        {/* Voice TTS toggle */}
        {appMode === "traductor" && (
          <button
            onClick={() => setVoiceEnabled(!voiceEnabled)}
            title="Activar/Desactivar lectura de voz"
            className={`relative inline-flex items-center justify-center py-[6px] px-[14px] rounded-[6px] border transition-all duration-150 active:scale-95 cursor-pointer ${voiceEnabled
                ? "bg-[rgba(48,209,88,0.22)] border-[rgba(48,209,88,0.4)] text-[var(--success)] shadow-[0_0_8px_rgba(48,209,88,0.2)]"
                : "bg-[rgba(48,209,88,0.1)] border-[rgba(48,209,88,0.25)] text-[var(--success)] hover:bg-[rgba(48,209,88,0.18)]"
              }`}
          >
            {/* Invisible content keeps the exact same horizontal dimensions */}
            <span className="invisible text-[13px] font-medium select-none pointer-events-none">
              {voiceEnabled ? "🔊 Voz: Sí" : "🔇 Voz: No"}
            </span>
            {/* Absolute positioning to stack icon and text vertically inside the same space */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pt-[1px]">
              <span className="text-[16px] leading-none mb-[8px]">{voiceEnabled ? "🔊" : "🔇"}</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.02em] leading-none">{voiceEnabled ? "Voz: Sí" : "Voz: No"}</span>
            </div>
          </button>
        )}

        {/* PiP */}
        {appMode === "traductor" && (
          <button
            onClick={onTogglePip}
            title="Ventana PiP flotante"
            className={`inline-flex items-center justify-center gap-[6px] py-[6px] px-[14px] rounded-[6px] border text-[13px] font-medium transition-all duration-150 active:scale-95 cursor-pointer ${pipActive
                ? "bg-[var(--accent-muted)] border-[var(--accent)]/40 text-[var(--accent)] shadow-[0_0_8px_rgba(0,122,255,0.2)]"
                : "bg-[var(--surface)] border-[var(--border)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)]"
              }`}
          >
            ⧉ PiP
          </button>
        )}

        {/* Docs */}
        <button
          onClick={onOpenDocs}
          title="Documentación"
          className="inline-flex items-center justify-center gap-[6px] py-[6px] px-[14px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)] transition-all duration-150 active:scale-95 cursor-pointer text-[13px]"
        >
          📖 Docs
        </button>

        {/* AI Summary */}
        {appMode === "traductor" && (
          <button
            onClick={onOpenSummary}
            disabled={meetingTranscriptLength === 0}
            title="Generar minuta con IA"
            className="inline-flex items-center justify-center gap-[6px] py-[6px] px-[14px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)] transition-all duration-150 active:scale-95 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:active:scale-100 text-[13px]"
          >
            📝 Minuta
          </button>
        )}

        {/* Theme toggle */}
        <button
          onClick={onToggleTheme}
          title="Cambiar tema visual"
          className="inline-flex items-center justify-center py-[6px] px-[14px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)] transition-all duration-150 active:scale-95 cursor-pointer text-xs"
        >
          {theme === "dark" ? "☀️" : "🌙"}
        </button>

        {/* Logout */}
        <button
          onClick={onLogout}
          title="Cerrar sesión"
          className="inline-flex items-center justify-center py-[6px] px-[14px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] text-rose-400/70 hover:bg-[var(--surface-hover)] hover:text-rose-400 transition-all duration-150 active:scale-95 cursor-pointer"
        >
          <svg className="w-4 h-4 fill-none stroke-current" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
        </button>
      </div>
    </header>
  );
};
